import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAdminAuth } from '../auth';
import { useDistrict } from '../districts-admin';
import {
  archiveSession,
  createSession,
  reorderSessions,
  unarchiveSession,
  updateSession,
  useSessions,
  type SessionListItem,
} from './useSessions';

/**
 * セッション管理画面 (admin 第 16 弾、Phase 5)。
 *
 * 旧 `old/apps/admin/src/pages/SessionsPage.tsx` (280 行) の form + active / archived 一覧を
 * v2 callable 不要・Firestore 直接 CRUD で踏襲。
 *
 * 旧との差分:
 *   - endDate (date) → startAt + endAt (datetime-local) に拡張 (旧は startAt=now 固定だったが、
 *     v2 では admin が事前にセッションを登録できるよう開始日時も明示)
 *   - archive 確認文言を旧の長文 confirm から terse 形へ簡略化 (shared-ui Modal を使わず confirm 継続)
 *
 * 認可: master / district admin (担当地区) のみ操作可。rules で deny される (UI でも guard)。
 */

function isActive(item: SessionListItem, now = new Date()): boolean {
  if (item.archivedAt) return false;
  if (item.startAt && item.startAt.getTime() > now.getTime()) return false;
  if (item.endAt && item.endAt.getTime() < now.getTime()) return false;
  return true;
}

/** datetime-local input ↔ ISO8601 変換 (local tz を尊重した変換)。 */
function localToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function isoToLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function SessionsAdminPage() {
  const { districtId } = useParams<{ districtId: string }>();
  const { state: authState } = useAdminAuth();
  const districtState = useDistrict(districtId ?? null);
  const sessionsState = useSessions(districtId ?? null);
  const { toast } = useToast();

  const [name, setName] = useState('');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState(() => {
    const end = new Date();
    end.setDate(end.getDate() + 30);
    return isoToLocal(end.toISOString());
  });
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  // 編集中セッション (id) と編集フォームの draft。
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editStartAt, setEditStartAt] = useState('');
  const [editEndAt, setEditEndAt] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // DnD: ドラッグ中の id / ドラッグオーバー中の id (視覚フィードバック用) と並び替え保存中フラグ。
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);

  const isMaster = authState.role === 'master';
  const isDistrictAdminOfThis =
    authState.role === 'district' && !!districtId && authState.districts.includes(districtId);
  const canManage = isMaster || isDistrictAdminOfThis;
  const uid = authState.user?.uid ?? '';

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    if (!districtId || !canManage) return;
    if (!name.trim()) {
      toast('error', 'セッション名を入力してください');
      return;
    }
    setSubmitting(true);
    try {
      const { code } = await createSession(
        districtId,
        {
          name,
          startAt: localToIso(startAt),
          endAt: localToIso(endAt),
        },
        uid,
      );
      toast('success', `セッションを作成しました (コード: ${code})`);
      setName('');
    } catch (e) {
      toast('error', `作成失敗: ${(e as Error).message}`);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleArchive(item: SessionListItem) {
    if (!districtId || !canManage) return;
    if (
      !confirm(
        `セッション「${item.name}」を非表示にしますか？\n` +
          `・参加 QR / Onboarding / フィルタ / 分析からは除外されます\n` +
          `・既存投稿の sessionId は維持されます (履歴は壊れません)\n` +
          `・このページの「復帰」操作で再度表示できます`,
      )
    ) {
      return;
    }
    setBusyId(item.id);
    try {
      await archiveSession(districtId, item.id, uid);
      toast('success', 'セッションを非表示にしました');
    } catch (e) {
      toast('error', `非表示失敗: ${(e as Error).message}`);
    } finally {
      setBusyId(null);
    }
  }

  async function handleUnarchive(item: SessionListItem) {
    if (!districtId || !canManage) return;
    if (!confirm(`セッション「${item.name}」を復帰させますか？`)) return;
    setBusyId(item.id);
    try {
      await unarchiveSession(districtId, item.id, uid);
      toast('success', 'セッションを復帰しました');
    } catch (e) {
      toast('error', `復帰失敗: ${(e as Error).message}`);
    } finally {
      setBusyId(null);
    }
  }

  function startEdit(item: SessionListItem) {
    setEditingId(item.id);
    setEditName(item.name);
    setEditStartAt(item.startAt ? isoToLocal(item.startAt.toISOString()) : '');
    setEditEndAt(item.endAt ? isoToLocal(item.endAt.toISOString()) : '');
  }

  function cancelEdit() {
    setEditingId(null);
  }

  async function handleEditSave(ev: FormEvent<HTMLFormElement>, item: SessionListItem) {
    ev.preventDefault();
    if (!districtId || !canManage) return;
    if (!editName.trim()) {
      toast('error', 'セッション名を入力してください');
      return;
    }
    const startIso = localToIso(editStartAt);
    const endIso = localToIso(editEndAt);
    if (!startIso) {
      toast('error', '開始日時を入力してください');
      return;
    }
    if (!endIso) {
      toast('error', '終了日時を入力してください');
      return;
    }
    setEditSaving(true);
    try {
      await updateSession(
        districtId,
        item.id,
        { name: editName, startAt: startIso, endAt: endIso },
        uid,
      );
      toast('success', 'セッションを更新しました');
      setEditingId(null);
    } catch (e) {
      toast('error', `更新失敗: ${(e as Error).message}`);
    } finally {
      setEditSaving(false);
    }
  }

  /**
   * DnD ドロップ確定: draggingId を targetId の位置へ挿入した新しい順序を作り、
   * その順序で sortOrder を 0,1,2... に振り直して一括保存。
   */
  async function handleDrop(targetItem: SessionListItem, orderedActive: SessionListItem[]) {
    setDragOverId(null);
    const sourceId = draggingId;
    setDraggingId(null);
    if (!districtId || !canManage || !sourceId || sourceId === targetItem.id) return;

    const ids = orderedActive.map((s) => s.id);
    const from = ids.indexOf(sourceId);
    const to = ids.indexOf(targetItem.id);
    if (from < 0 || to < 0) return;
    ids.splice(from, 1);
    ids.splice(to, 0, sourceId);

    setReordering(true);
    try {
      await reorderSessions(districtId, ids, uid);
      toast('success', '表示順を更新しました');
    } catch (e) {
      toast('error', `並び替え失敗: ${(e as Error).message}`);
    } finally {
      setReordering(false);
    }
  }

  const items = sessionsState.status === 'ready' ? sessionsState.items : [];
  const activeItems = items.filter((s) => !s.archivedAt);
  const archivedItems = items.filter((s) => !!s.archivedAt);

  return (
    <>
      <header className="mb-6">
        <Link
          to={districtId ? `/districts/${districtId}` : '/districts'}
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          ← 地区詳細
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 mt-2">
          セッション管理
          {districtState.status === 'ready' ? ` — ${districtState.district.name}` : ''}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          まち歩きセッションの登録・非表示。Announcement.targetSessionIds の対象一覧にも使われる。
        </p>
      </header>

      {!canManage && (
        <Card padding="md" className="border-danger-600 mb-4">
          <p className="text-sm text-danger-700">
            この地区のセッション管理権限がありません (role: {authState.role ?? 'none'}、地区:{' '}
            {districtId})。
          </p>
        </Card>
      )}

      <Card padding="lg" className="mb-6">
        <h2 className="text-base font-bold text-slate-900 mb-3">新規セッション</h2>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-3">
          <label className="grid grid-cols-1 gap-1">
            <span className="text-xs text-slate-500">セッション名 *</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              required
              disabled={!canManage || submitting}
              placeholder="例: 2026-04-15 午前グループA"
              className="min-h-touch px-3 border border-slate-300 rounded text-sm"
            />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="grid grid-cols-1 gap-1">
              <span className="text-xs text-slate-500">開始日時 (空欄 = 即時)</span>
              <input
                type="datetime-local"
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
                disabled={!canManage || submitting}
                className="min-h-touch px-3 border border-slate-300 rounded text-sm"
              />
            </label>
            <label className="grid grid-cols-1 gap-1">
              <span className="text-xs text-slate-500">終了日時 (空欄 = 開始 + 30 日)</span>
              <input
                type="datetime-local"
                value={endAt}
                onChange={(e) => setEndAt(e.target.value)}
                disabled={!canManage || submitting}
                className="min-h-touch px-3 border border-slate-300 rounded text-sm"
              />
            </label>
          </div>
          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={!canManage || submitting}>
              {submitting ? '作成中…' : '➕ 作成'}
            </Button>
          </div>
        </form>
      </Card>

      <h2 className="text-base font-bold text-slate-900 mb-3">アクティブなセッション</h2>

      {sessionsState.status === 'loading' && (
        <Card padding="md">
          <p className="text-sm text-slate-600">読込中…</p>
        </Card>
      )}

      {sessionsState.status === 'error' && (
        <Card padding="md" className="border-danger-600">
          <p className="text-sm text-danger-700">読込エラー: {sessionsState.error.message}</p>
        </Card>
      )}

      {sessionsState.status === 'ready' && activeItems.length === 0 && (
        <Card padding="md">
          <p className="text-sm text-slate-600">アクティブなセッションはありません。</p>
        </Card>
      )}

      {sessionsState.status === 'ready' && activeItems.length > 1 && canManage && (
        <p className="text-xs text-slate-500 mb-2">
          行をドラッグ&ドロップで並び替えできます (この順番でユーザー側にも表示されます)。
        </p>
      )}

      {sessionsState.status === 'ready' && activeItems.length > 0 && (
        <ul className="grid grid-cols-1 gap-2">
          {activeItems.map((s) => {
            const active = isActive(s);
            const isEditing = editingId === s.id;
            const isDragging = draggingId === s.id;
            const isDragOver = dragOverId === s.id && draggingId !== null && draggingId !== s.id;
            return (
              <li
                key={s.id}
                draggable={canManage && !isEditing}
                onDragStart={() => {
                  if (!canManage || isEditing) return;
                  setDraggingId(s.id);
                }}
                onDragEnd={() => {
                  setDraggingId(null);
                  setDragOverId(null);
                }}
                onDragOver={(e) => {
                  if (!canManage || draggingId === null) return;
                  e.preventDefault();
                  if (dragOverId !== s.id) setDragOverId(s.id);
                }}
                onDragLeave={() => {
                  if (dragOverId === s.id) setDragOverId(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  void handleDrop(s, activeItems);
                }}
              >
                <Card
                  padding="md"
                  className={[
                    'flex items-start gap-3 transition-colors',
                    canManage && !isEditing ? 'cursor-move' : '',
                    isDragging ? 'opacity-50 ring-2 ring-primary-400' : '',
                    isDragOver ? 'ring-2 ring-primary-500 bg-primary-50' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {canManage && !isEditing && (
                    <span
                      aria-hidden
                      className="shrink-0 select-none text-slate-400 text-base leading-none pt-0.5"
                      title="ドラッグして並び替え"
                    >
                      ⠿
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    {isEditing ? (
                      <form
                        onSubmit={(e) => void handleEditSave(e, s)}
                        className="grid grid-cols-1 gap-3"
                      >
                        <label className="grid grid-cols-1 gap-1">
                          <span className="text-xs text-slate-500">セッション名 *</span>
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            maxLength={80}
                            required
                            disabled={editSaving}
                            className="min-h-touch px-3 border border-slate-300 rounded text-sm"
                          />
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <label className="grid grid-cols-1 gap-1">
                            <span className="text-xs text-slate-500">開始日時</span>
                            <input
                              type="datetime-local"
                              value={editStartAt}
                              onChange={(e) => setEditStartAt(e.target.value)}
                              disabled={editSaving}
                              className="min-h-touch px-3 border border-slate-300 rounded text-sm"
                            />
                          </label>
                          <label className="grid grid-cols-1 gap-1">
                            <span className="text-xs text-slate-500">終了日時</span>
                            <input
                              type="datetime-local"
                              value={editEndAt}
                              onChange={(e) => setEditEndAt(e.target.value)}
                              disabled={editSaving}
                              className="min-h-touch px-3 border border-slate-300 rounded text-sm"
                            />
                          </label>
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={cancelEdit}
                            disabled={editSaving}
                          >
                            キャンセル
                          </Button>
                          <Button type="submit" size="sm" disabled={editSaving}>
                            {editSaving ? '保存中…' : '保存'}
                          </Button>
                        </div>
                      </form>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-bold text-slate-900">{s.name}</span>
                          {active && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-success-600 text-white">
                              開催中
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-[11px] text-slate-500">
                          ID: <code>{s.id}</code> / コード: <code>{s.code}</code>
                        </p>
                        <p className="mt-1 text-[11px] text-slate-500">
                          {s.startAt ? s.startAt.toLocaleString('ja-JP') : '即時'} 〜{' '}
                          {s.endAt ? s.endAt.toLocaleString('ja-JP') : '無期限'}
                        </p>
                        <p className="mt-1 text-[11px] text-slate-500">投稿: {s.postCount}</p>
                      </>
                    )}
                  </div>
                  {canManage && !isEditing && (
                    <div className="shrink-0 flex flex-col gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => startEdit(s)}
                        disabled={busyId === s.id || reordering}
                      >
                        ✏️ 編集
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => void handleArchive(s)}
                        disabled={busyId === s.id || reordering}
                      >
                        📦 非表示
                      </Button>
                    </div>
                  )}
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      {sessionsState.status === 'ready' && archivedItems.length > 0 && (
        <details className="mt-6">
          <summary className="cursor-pointer text-sm font-medium text-slate-600">
            ▼ 非表示中のセッション ({archivedItems.length} 件)
          </summary>
          <p className="text-xs text-slate-500 mt-2 mb-2">
            ユーザー側からは見えていませんが、既存投稿の sessionId
            は維持されています。「復帰」で再表示できます。
          </p>
          <ul className="grid grid-cols-1 gap-2">
            {archivedItems.map((s) => (
              <li key={s.id}>
                <Card padding="md" className="flex items-start gap-3 opacity-75">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-slate-700">{s.name}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-300 text-slate-700">
                        非表示
                      </span>
                    </div>
                    <p className="mt-1 text-[11px] text-slate-500">
                      ID: <code>{s.id}</code> / コード: <code>{s.code}</code>
                    </p>
                    <p className="mt-1 text-[11px] text-slate-500">
                      {s.startAt ? s.startAt.toLocaleString('ja-JP') : '即時'} 〜{' '}
                      {s.endAt ? s.endAt.toLocaleString('ja-JP') : '無期限'}
                    </p>
                    <p className="mt-1 text-[11px] text-slate-500">投稿: {s.postCount}</p>
                    {s.archivedAt && (
                      <p className="mt-1 text-[11px] text-slate-400">
                        非表示にした日時: {s.archivedAt.toLocaleString('ja-JP')}
                      </p>
                    )}
                  </div>
                  {canManage && (
                    <div className="shrink-0">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => void handleUnarchive(s)}
                        disabled={busyId === s.id}
                      >
                        🔄 復帰
                      </Button>
                    </div>
                  )}
                </Card>
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
}
